import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';

import * as moment from 'moment';

import { HsgModal } from '@hhsg/hsg-asc';

import { Constants } from 'src/shared/utilities/constants.util';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterService } from 'src/shared/services/cost-center.service';
import { NoStaffingGridModalComponent } from '../no-staffing-grid-modal/no-staffing-grid-modal.component';
import { RouteUtil } from 'src/shared/utilities/route.util';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { ShiftBlockDay } from 'src/shared/models/shift-block-day.model';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPageUtil } from '../base-classes/workflow-page/workflow-page.util';
import { WorkflowPlannerService } from 'src/shared/services/workflow-planner.service';
import { Subscription } from 'rxjs';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { Client } from 'src/shared/models/client.model';
import { ClientService } from 'src/shared/services/client.service';

@Component({
    selector: 'smt-planner',
    templateUrl: './planner.component.html',
    styleUrls: ['./planner.component.scss']
})
export class PlannerComponent {
    client: Client;
    costCenter: CostCenter;
    currentPage: WorkflowPage;
    isFormValid = false;

    navigationSubscription: Subscription;
    isFirstTimePlannerLoad:boolean=false;
    previousShiftDate : string ;
    private isLoading = false;

    showFinishPage=false;
    constructor(
        private _costCenterService: CostCenterService,
        private _modal: HsgModal,
        private _route: ActivatedRoute, 
        private _router: Router,
        private _shiftBlockService: ShiftBlockService,
        private _activatedRoute: ActivatedRoute, 
        private _workflowPlannerService: WorkflowPlannerService,
        private _workflowUtil: WorkflowUtilityService,
        private _clientService: ClientService) { 
        this.navigationSubscription = this._router.events.subscribe(async (e) => {
            const routerEvent = e instanceof Scroll ? e.routerEvent : e;
            if (routerEvent  instanceof NavigationEnd) {
                let params = this._activatedRoute.snapshot.queryParamMap;
                let costCenterId = params.has('costCenterId') ? +params.get('costCenterId') : 0;
                let date = params.has('date') ? params.get('date') : null;
                let shiftBlockId = params.has('shiftBlockId') ? +params.get('shiftBlockId') : 0;
                let isLastShiftBlock = false;
                if(params.has('isLastShiftBlock')){
                   if(params.get('isLastShiftBlock')=='true'){isLastShiftBlock = true;}
                }
                

                if (!this.isLoading) {
                    this.isLoading = true;
                    await this.load(costCenterId, shiftBlockId, date, isLastShiftBlock);
                    this.isLoading = false;
                }
            }
        });
    }

    ngOnDestroy() {
        this.navigationSubscription.unsubscribe();
    }

    private done() {
     this.showFinishPage = true;
    }

    
    private async load(costCenterId: number, shiftBlockId: number, workflowTimeFacility: string, isLastShiftBlock: boolean) { 
        this.currentPage = null;
        let currentShiftBlock: ShiftBlock = null;

        if (shiftBlockId > 0) {
            currentShiftBlock = await this._shiftBlockService.loadShiftBlock(costCenterId, workflowTimeFacility);
        }
        else {
            let shiftBlockDay = await this.loadCurrentShiftBlock(costCenterId);

            if (shiftBlockDay) {
                currentShiftBlock = shiftBlockDay.shiftBlock;
                workflowTimeFacility = moment(shiftBlockDay.timezoneAdjustedDate).toString();
            }            
        }                

        if (currentShiftBlock) {
            let client = await this._clientService.GetClientById();
            let workflowtime = moment(workflowTimeFacility);
            let previousShift = await this._shiftBlockService.getPreviousShiftBlockDay(currentShiftBlock.costCenterId, workflowtime.format(Constants.FacilityTimeFormat));

            if (previousShift) {
                this.previousShiftDate = moment(previousShift.timezoneAdjustedDate).format(Constants.FacilityTimeFormat);
            }
            costCenterId = Math.max(costCenterId, currentShiftBlock.costCenterId);

            this.costCenter = await this._costCenterService.getCostCenter(costCenterId);
            let shiftRecordService = this._workflowUtil.getShiftRecordService(this.costCenter);

            if (this.costCenter && WorkflowPageUtil.hasInpatientConfiguration(this.costCenter)) { 
                await this._workflowPlannerService.configureInpatientWorkflow(shiftRecordService, currentShiftBlock, workflowTimeFacility, this.previousShiftDate, client);             
            }
            else if (this.costCenter && WorkflowPageUtil.hasAcuityWithRoomsConfiguration(this.costCenter)) { 
                await this._workflowPlannerService.configureAcuityWithRoomsWorkflow(shiftRecordService, currentShiftBlock, workflowTimeFacility,
                             this.costCenter.ancillaryConfiguration.enterUOSByBlock, client, this.previousShiftDate);
            }
            else if(this.costCenter && WorkflowPageUtil.hasAcuityConfiguration(this.costCenter)) {
                await this._workflowPlannerService.configureAcuityWorkflow(shiftRecordService, currentShiftBlock, workflowTimeFacility,
                     this.costCenter.ancillaryConfiguration.enterUOSByBlock, client, this.previousShiftDate);
            }
            else if(this.costCenter && WorkflowPageUtil.hasUosConfiguration(this.costCenter)) {
                await this._workflowPlannerService.configureTotalUOSWorkflow(shiftRecordService, currentShiftBlock, workflowTimeFacility,
                     this.costCenter.ancillaryConfiguration.enterUOSByBlock, client, this.previousShiftDate, isLastShiftBlock);
            }
            else {
                this.openStaffingGridModal('Missing configuration', 'This cost center is missing a valid staffing plan configuration.');
                return;
            }
        }
        else if(this.isFirstTimePlannerLoad) {
            this._router.navigateByUrl(RouteUtil.getRouteByPage(Constants.SelectCostCenter));
            return;
           
        }
        else {
            this.openStaffingGridModal('No Staffing Grid', 'There is no staffing grid configured for this cost center.');
            return;
        }

        if (!this._workflowPlannerService.isWorkflowValid()) {
            this.done();
            return;
        }

        this.currentPage = this._workflowPlannerService.start();
    }

    private loadCurrentShiftBlock(costCenterId: number) : Promise<ShiftBlockDay> {
        if (costCenterId === 0) {
            return this._costCenterService.getPlannerCostCenter().then((plannerCostCenterId: number) => {
                if (plannerCostCenterId && plannerCostCenterId != 0) {
                    return this._shiftBlockService.loadCurrentShiftBlock(plannerCostCenterId);
                } 
                else {
                    this.isFirstTimePlannerLoad = true;
                    return Promise.resolve(null);
                }
            });
        } 
        else {
            return this._shiftBlockService.loadCurrentShiftBlock(costCenterId);
        }
    }

    private async next(shiftRecordId: number) {
        let costCenter = this.currentPage.shiftRecord.costCenter;
        let shiftBlockId = this.currentPage.shiftRecord.shiftBlockId;
        let workflowTimeFacility = this.currentPage.shiftRecord.workflowTimeFacility;

        this.currentPage = null;
        let shiftRecordService = this._workflowUtil.getShiftRecordService(this.costCenter);
        
        await shiftRecordService.getActive(costCenter.id, shiftBlockId, workflowTimeFacility,this.previousShiftDate).then(updatedShiftRecord => {
            this._workflowPlannerService.workflow.pages.forEach(p => {
                p.shiftRecord = { ...updatedShiftRecord };
                p.shiftRecord.id = shiftRecordId;   
            });

            this._workflowPlannerService.next();
            this.currentPage = this._workflowPlannerService.currentPage;
        }); 
    }

    private openStaffingGridModal(title: string, content: string) {
        this._modal.open(NoStaffingGridModalComponent, {
            data: {
                title: title,
                content: content,
                acceptText: 'Select Another Cost Center',
                size: { width: '500px', height: '100px' },
                acceptLink: Constants.CostCenterWorkflowUrl,
                rejectText: 'Configure Cost Center',
                rejectLink: RouteUtil.getRouteByPage(Constants.CostCenter)
            }
        });
    }

    private previous() {
        this._workflowPlannerService.previous();
        this.currentPage = this._workflowPlannerService.currentPage;
    }

    private skip() {
        this._workflowPlannerService.skip();
        this.currentPage = this._workflowPlannerService.currentPage;
    }
}
